import { useObjectStore } from "@shared/store/objectStore";
import { useEffect } from "react";

interface ObjectItem {
  n_ob: number;
  syb_rnk: number;
}

interface UseGlobalObjectOptions {
  /**
   * When true (default), auto-reselects first object if user clears the selection.
   * Set to false for pages that support "all objects" mode (e.g. communications).
   */
  required?: boolean;
}

/**
 * Global object selection hook.
 * Reads/writes selectedObject from the global Zustand store.
 * Auto-selects the first object on first visit.
 * When `required` is true (default), re-selects if user clears.
 * Always resets to first if current selection is removed from the list (RBAC change).
 */
export function useGlobalObject(objects: ObjectItem[], opts?: UseGlobalObjectOptions) {
  const required = opts?.required ?? true;
  const selectedObject = useObjectStore((s) => s.selectedObject);
  const initialized = useObjectStore((s) => s._initialized);
  const setSelectedObject = useObjectStore((s) => s.setSelectedObject);

  useEffect(() => {
    if (objects.length === 0) return;

    if (!selectedObject) {
      // Auto-select first: on first visit always, after clear only if required
      if (!initialized || required) {
        const first = objects[0];
        setSelectedObject({ objectId: first.n_ob, regionId: first.syb_rnk });
        if (!initialized) useObjectStore.setState({ _initialized: true });
      }
      return;
    }

    // Mark initialized once we have a selection
    if (!initialized) useObjectStore.setState({ _initialized: true });

    // Selected object no longer in available list (RBAC change) → reset to first
    const exists = objects.some(
      (o) => o.n_ob === selectedObject.objectId && o.syb_rnk === selectedObject.regionId,
    );
    if (!exists) {
      const first = objects[0];
      setSelectedObject({ objectId: first.n_ob, regionId: first.syb_rnk });
    }
  }, [objects, selectedObject, setSelectedObject, required, initialized]);

  return { selectedObject, setSelectedObject };
}
